import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import GetSurveys from "../queries/GetSurveys";
import Select from "react-select";
import Spinner from "./Spinner";
import writeXlsxFile from "write-excel-file";
import { useAppContext } from "../AppContext";

import {
  Table,
  Row,
  Col,
  Card,
  CardHeader,
  FormGroup,
  CardBody,
  Button,
} from "reactstrap";

const OpenResponses = () => {
  const imi_questions = [
    {
      key: "recommendations",
      type: "string",
      default: "",
      text: "What recommendations do you have for making this module more effective? (Please make your suggested changes as specific as you can.)",
    },
    {
      key: "observations",
      type: "string",
      default: "",
      text: "What observations do you have about this module or the method in which it was delivered that have not been addressed by other questions?",
    },
    {
      key: "challenging_aspects",
      type: "string",
      default: "",
      text: "What were the most challenging aspects or sections of the module?",
    },
    {
      key: "most_useful",
      type: "string",
      default: "",
      text: "What part(s) of the module seem most useful to you in your position as a FAO?",
    },
    {
      key: "least_useful",
      type: "string",
      default: "",
      text: "What part(s) of the module seem least useful to you in your position as a FAO?",
    },
  ];

  const test_questions = [
    {
      key: "recommendations",
      type: "string",
      default: "",
      text: "What recommendations do you have for making this assessment (or posttest) more effective? (Please make your suggested changes as specific as you can.)",
    },
    {
      key: "observations",
      type: "string",
      default: "",
      text: "What observations do you have about this assessment (or posttest) or the method in which it was delivered that have not been addressed by other questions?",
    },
  ];
  const { error, loading, data, refetch } = useQuery(GetSurveys, {
    variables: {
      survey_language: null,
      trial_type: null,
      survey_type: null,
    },
  });
  const [selectedLanguage, setSelectedLanguage] = useState({
    value: null,
    label: "None",
  });
  const [selectedTrialType, setSelectedTrialType] = useState({
    value: null,
    label: "None",
  });
  const [selectedSurveyType, setSelectedSurveyType] = useState({
    value: null,
    label: "None",
  });
  const dataToDisplay = useRef(null);
  const { displayToast, closeToast } = useAppContext();
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const runRefetch = async () => {
      let variables;

      if (
        (!selectedLanguage || selectedLanguage.value === null) &&
        (!selectedTrialType || selectedTrialType.value === null) &&
        (!selectedSurveyType || selectedSurveyType.value === null)
      ) {
        variables = {
          survey_language: null,
          trial_type: null,
          survey_type: null,
        };
      } else {
        variables = {
          survey_language: selectedLanguage?.value ?? null,
          trial_type: selectedTrialType?.value ?? null,
          survey_type: selectedSurveyType?.value ?? null,
        };
      }

      dataToDisplay.current = null;
      setIsFetching(true);

      try {
        await refetch(variables);
      } finally {
        setIsFetching(false);
      }
    };

    runRefetch();
  }, [selectedLanguage, selectedTrialType, selectedSurveyType, refetch]);

  if (loading) return <Spinner />;
  if (error) {
    console.log("error:");
    console.log(error);
    return <p>Something Went Wrong...</p>;
  }

  const { surveys } = data;
  console.log("got surveys: ", surveys);

  // set up what data is to be displayed here now that surveys are available
  if (surveys.length !== 0) {
    if (dataToDisplay.current === null || dataToDisplay.current.length > 0)
      dataToDisplay.current = [];

    surveys.forEach((survey, sIndex) => {
      if (!checkCompletionStatus(survey)) return;

      const existingData = dataToDisplay.current.find(
        (data) => data.title === survey.title,
      );

      const thisData = existingData
        ? existingData
        : {
            title: survey.title,
            questions: [],
          };

      let questionsToCheck = [];

      if (
        survey.survey_type === null ||
        survey.survey_type === "imi" ||
        survey.survey_type === "introduction"
      ) {
        questionsToCheck = imi_questions;
      } else {
        questionsToCheck = test_questions;
      }

      questionsToCheck.forEach((question, qIndex) => {
        if (question.type !== "string") {
          console.log("WHY IS THIS QUESTION NOT OF TYPE STRING???");
          return;
        }

        const existingQuestion = thisData.questions.find(
          (thisQuestion) => thisQuestion.title === question.text,
        );

        const addQuestionData = (question, response, user_name) => {
          question.responses.push({
            user_name,
            text: response,
          });
        };

        if (existingQuestion) {
          addQuestionData(
            existingQuestion,
            survey[question.key],
            survey.user.user_name,
          );
        } else {
          const newQuestion = {
            title: question.text,
            responses: [],
          };

          addQuestionData(
            newQuestion,
            survey[question.key],
            survey.user.user_name,
          );

          thisData.questions.push(newQuestion);
        }
      });

      if (!existingData) dataToDisplay.current.push(thisData);
    });

    dataToDisplay.current.sort((a, b) => {
      const languageA = getLanguage(a.title);
      const languageB = getLanguage(b.title);
      const priorityA = getContentPriority(a.title);
      const priorityB = getContentPriority(b.title);

      if (languageA !== languageB) {
        return languageA.localeCompare(languageB); // Sort by language
      }

      if (priorityA !== priorityB) {
        return priorityA - priorityB; // Sort by content priority
      }

      // If the same category, sort titles alphabetically
      return a.title.localeCompare(b.title);
    });
  } else {
    dataToDisplay.current = [];
  }

  // Function to assign priority to different course components
  function getContentPriority(title) {
    if (title.includes("Introduction")) return 1;
    if (title.includes("Module")) return 2;
    if (title.includes("Posttest")) return 3;
    return 4;
  }

  // Extract language from title
  function getLanguage(title) {
    if (title.includes("Arabic")) return "Arabic";
    if (title.includes("Russian")) return "Russian";
    if (title.includes("Korean")) return "Korean";
    return "Other";
  }

  function checkCompletionStatus(survey) {
    if (survey.completed_date != null) {
      return true;
    }

    return false;
  }

  function displayQuestions(data, dIndex) {
    console.log("trying to display: ", data);

    return data.questions.map((question, qIndex) => {
      return (
        <tbody key={`question_${dIndex}_${qIndex}`}>
          <tr>
            <th
              scope="col"
              style={{ backgroundColor: "lightgray" }}
              colSpan={6}
            >
              <div
                className="d-flex justify-content-center font-weight-bold"
                style={{
                  fontSize: ".85rem",
                }}
              >
                {question.title}
              </div>
            </th>
          </tr>
          {question.responses.map((response, rIndex) => {
            return (
              <tr key={`response_${dIndex}_${qIndex}_${rIndex}`}>
                <td style={{ width: "1%" }}>{response.user_name}</td>
                <td style={{ whiteSpace: "normal" }}>{response.text}</td>
              </tr>
            );
          })}
        </tbody>
      );
    });
  }

  function displayData() {
    return dataToDisplay.current.map((data, dIndex) => {
      return (
        <React.Fragment key={`dataToDisplay_${dIndex}`}>
          <Table bordered striped>
            <thead>
              <tr>
                <th
                  scope="col"
                  style={{ backgroundColor: "lightgray" }}
                  colSpan={6}
                >
                  <div
                    className="d-flex justify-content-center font-weight-bold"
                    style={{
                      fontSize: ".85rem",
                    }}
                  >
                    {data.title}
                  </div>
                </th>
              </tr>
            </thead>
            {displayQuestions(data, dIndex)}
          </Table>
        </React.Fragment>
      );
    });
  }

  function generateExcel() {
    let success =
      dataToDisplay.current !== null && dataToDisplay.current.length !== 0;

    const toast = {
      title: success ? "Success" : "Error",
      color: success ? "bg-success" : "bg-danger",
      icon: success ? "check-bold" : "fat-remove",
      message: success ? "XML is being generated" : "No data to place in XML",
    };

    displayToast(toast);

    setTimeout(() => {
      closeToast();
    }, 5000);

    if (!success) return;

    console.log("dataToDisplay: ", dataToDisplay.current);

    let tableData = [];

    dataToDisplay.current.forEach((data, dIndex) => {
      // objects.push(data.problems);
      // schemas.push(schema)
      // sheets.push(data.title.replace(/^Course I, /, '').slice(0, 30));

      tableData.push([
        {
          value: data.title,
          span: 2,
          backgroundColor: "#484a5c",
          color: "#ffffff",
          align: "center",
          width: 36,
        },
        null,
      ]);

      data.questions.forEach((question, qIndex) => {
        tableData.push([
          {
            type: String,
            value: question.title,
            width: 36,
            span: 2,
            backgroundColor: "#a8aabd",
            color: "#ffffff",
            wrap: true,
          },
          null,
        ]);

        question.responses.forEach((response, rIndex) => {
          tableData.push([
            {
              type: String,
              value: response.user_name,
              width: 10,
            },
            {
              type: String,
              value: response.text,
              width: 26,
            },
          ]);
        });
      });
    });

    // Get the current date
    const currentDate = new Date();

    // Format the date as YYYY-MM-DD
    const formattedDate = currentDate.toISOString().split("T")[0]; // This will give you a string like "2023-04-01"

    // Create the filename by including the formatted date
    const fileName = `open_responses_${formattedDate}.xlsx`;

    // Use the fileName in your writeXlsxFile function
    writeXlsxFile(tableData, {
      fileName: fileName,
    });
  }

  function generateSortableExcel() {
    let success =
      dataToDisplay.current !== null && dataToDisplay.current.length !== 0;

    const toast = {
      title: success ? "Success" : "Error",
      color: success ? "bg-success" : "bg-danger",
      icon: success ? "check-bold" : "fat-remove",
      message: success ? "XML is being generated" : "No data to place in XML",
    };

    displayToast(toast);

    setTimeout(() => {
      closeToast();
    }, 5000);

    if (!success) return;

    console.log("dataToDisplay: ", dataToDisplay.current);

    let tableData = [];

    //add header used for whole thing
    tableData.push([
      {
        value: "Survey Name",
        backgroundColor: "#484a5c",
        color: "#ffffff",
        align: "center",
        width: "12",
      },
      {
        value: "Question",
        backgroundColor: "#484a5c",
        color: "#ffffff",
        align: "center",
        width: "12",
      },
      {
        value: "Participant",
        backgroundColor: "#484a5c",
        color: "#ffffff",
        align: "center",
        width: "12",
      },
      {
        value: "Response",
        backgroundColor: "#484a5c",
        color: "#ffffff",
        align: "center",
        width: "12",
      },
    ]);

    let row_count = 0;
    dataToDisplay.current.forEach((data, dIndex) => {
      const survey_name = data.title;

      data.questions.forEach((question, qIndex) => {
        const question_title = question.title;

        question.responses.forEach((response, rIndex) => {
          tableData.push([
            {
              type: String,
              value: survey_name,
              width: 12,
              alignVertical: "center",
              wrap: true,
              backgroundColor: row_count % 2 !== 0 ? "#9bc1c4" : "#FFFFFF",
            },
            {
              type: String,
              value: question_title,
              width: 12,
              alignVertical: "center",
              wrap: true,
              backgroundColor: row_count % 2 !== 0 ? "#9bc1c4" : "#FFFFFF",
            },
            {
              type: String,
              value: response.user_name,
              alignVertical: "center",
              align: "center",
              width: 12,
              backgroundColor: row_count % 2 !== 0 ? "#9bc1c4" : "#FFFFFF",
            },
            {
              type: String,
              value: response.text,
              width: 12,
              alignVertical: "center",
              wrap: true,
              backgroundColor: row_count % 2 !== 0 ? "#9bc1c4" : "#FFFFFF",
            },
          ]);

          row_count++;
        });
      });
    });

    // Get the current date
    const currentDate = new Date();

    // Format the date as YYYY-MM-DD
    const formattedDate = currentDate.toISOString().split("T")[0]; // This will give you a string like "2023-04-01"

    // Create the filename by including the formatted date
    const fileName = `open_responses_columns_${formattedDate}.xlsx`;

    // Use the fileName in your writeXlsxFile function
    writeXlsxFile(tableData, {
      fileName: fileName,
    });
  }

  return (
    <>
      <Button color="primary" onClick={() => generateExcel()}>
        Create Excel
      </Button>
      <Button color="primary" onClick={() => generateSortableExcel()}>
        Create Columned Excel
      </Button>
      <Card className="bg-secondary shadow">
        <CardHeader className="bg-white border-0">
          <Row className="align-items-center">
            <Col sm="6" lg="4">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-language">
                  Language
                </label>
                <Select
                  value={selectedLanguage}
                  onChange={(selectedLanguage) => {
                    setSelectedLanguage(selectedLanguage);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "arabic",
                      label: "Arabic",
                    },
                    {
                      value: "korean",
                      label: "Korean",
                    },
                    {
                      value: "russian",
                      label: "Russian",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-language"
                />
              </FormGroup>
            </Col>
            <Col sm="6" lg="4">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-trial">
                  Trial Type
                </label>
                <Select
                  value={selectedTrialType}
                  onChange={(selectedTrialType) => {
                    setSelectedTrialType(selectedTrialType);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "individual",
                      label: "Individual",
                    },
                    {
                      value: "group",
                      label: "Group",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-trial"
                />
              </FormGroup>
            </Col>
            <Col sm="6" lg="4">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-survey">
                  Survey Type
                </label>
                <Select
                  value={selectedSurveyType}
                  onChange={(selectedSurveyType) => {
                    setSelectedSurveyType(selectedSurveyType);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "imi",
                      label: "IMI",
                    },
                    {
                      value: "introduction",
                      label: "Introduction",
                    },
                    {
                      value: "assessment",
                      label: "Assessment",
                    },
                    {
                      value: "posttest",
                      label: "Posttest",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-survey"
                />
              </FormGroup>
            </Col>
          </Row>
        </CardHeader>
        <CardBody className="p-0">
          {dataToDisplay.current !== null &&
          dataToDisplay.current.length > 0 ? (
            <>
              <div className="d-flex justify-content-center py-2 w-100 font-weight-bold table-dark">
                Course II
              </div>
              <div className="table-responsive">{displayData()}</div>
            </>
          ) : isFetching ? (
            <div className="d-flex flex-column p-5 my-5 h-100 w-100 justify-content-center align-items-center">
              <Spinner />
              <h3 className="text-muted mt-3">Loading results...</h3>
            </div>
          ) : (
            <div className="d-flex flex-column p-5 my-5 h-100 w-100 justify-content-center align-items-center">
              <h1>
                <i className="ni ni-bell-55 text-muted" />
              </h1>
              <h3 className="text-muted">
                Please select what you'd like to view
              </h3>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default OpenResponses;
